import { z } from 'zod';

/**
 * Pricing Rules Configuration DTOs
 *
 * Configurable rate calculation rules for private lending.
 * Allows businesses to customize their pricing formulas through the Control Center.
 *
 * @see Issue #XXX - Configurable Rate Calculator
 */

// =============================================================================
// ADJUSTMENT TIER SCHEMAS
// =============================================================================

/**
 * Comparison operators for tier conditions
 */
export const ComparisonOperatorSchema = z.enum([
  'eq', // equals
  'neq', // not equals
  'lt', // less than
  'lte', // less than or equal
  'gt', // greater than
  'gte', // greater than or equal
  'between', // between two values
  'in', // in array of values
]);

export type ComparisonOperator = z.infer<typeof ComparisonOperatorSchema>;

/**
 * Tier condition for determining when an adjustment applies
 */
export const TierConditionSchema = z.object({
  operator: ComparisonOperatorSchema,
  value: z.union([z.number(), z.string()]),
  maxValue: z.number().optional(), // For 'between' operator
});

export type TierCondition = z.infer<typeof TierConditionSchema>;

/**
 * A single adjustment tier (e.g., "FICO 760+ = -0.5%")
 */
export const AdjustmentTierSchema = z.object({
  id: z.string(),
  label: z.string(), // Display label: "Excellent Credit (760+)"
  condition: TierConditionSchema,
  adjustment: z.number(), // Rate adjustment: -0.5 or +0.25
  isEnabled: z.boolean().default(true),
});

export type AdjustmentTier = z.infer<typeof AdjustmentTierSchema>;

// =============================================================================
// ADJUSTMENT CATEGORY SCHEMAS
// =============================================================================

/**
 * FICO Score adjustment tiers
 */
export const FicoAdjustmentConfigSchema = z.object({
  isEnabled: z.boolean().default(true),
  tiers: z.array(AdjustmentTierSchema).default([
    {
      id: 'fico-760',
      label: 'Excellent (760+)',
      condition: { operator: 'gte', value: 760 },
      adjustment: -0.5,
      isEnabled: true,
    },
    {
      id: 'fico-740',
      label: 'Very Good (740-759)',
      condition: { operator: 'between', value: 740, maxValue: 759 },
      adjustment: -0.25,
      isEnabled: true,
    },
    {
      id: 'fico-720',
      label: 'Good (720-739)',
      condition: { operator: 'between', value: 720, maxValue: 739 },
      adjustment: 0,
      isEnabled: true,
    },
    {
      id: 'fico-700',
      label: 'Fair (700-719)',
      condition: { operator: 'between', value: 700, maxValue: 719 },
      adjustment: 0.25,
      isEnabled: true,
    },
    {
      id: 'fico-680',
      label: 'Below Average (680-699)',
      condition: { operator: 'between', value: 680, maxValue: 699 },
      adjustment: 0.5,
      isEnabled: true,
    },
    {
      id: 'fico-below',
      label: 'Poor (<680)',
      condition: { operator: 'lt', value: 680 },
      adjustment: 1.0,
      isEnabled: true,
    },
  ]),
});

export type FicoAdjustmentConfig = z.infer<typeof FicoAdjustmentConfigSchema>;

/**
 * LTV (Loan-to-Value) adjustment tiers
 */
export const LtvAdjustmentConfigSchema = z.object({
  isEnabled: z.boolean().default(true),
  tiers: z.array(AdjustmentTierSchema).default([
    {
      id: 'ltv-60',
      label: 'Low Risk (≤60%)',
      condition: { operator: 'lte', value: 60 },
      adjustment: -0.25,
      isEnabled: true,
    },
    {
      id: 'ltv-70',
      label: 'Standard (61-70%)',
      condition: { operator: 'between', value: 61, maxValue: 70 },
      adjustment: 0,
      isEnabled: true,
    },
    {
      id: 'ltv-75',
      label: 'Higher (71-75%)',
      condition: { operator: 'between', value: 71, maxValue: 75 },
      adjustment: 0.25,
      isEnabled: true,
    },
  ]),
});

export type LtvAdjustmentConfig = z.infer<typeof LtvAdjustmentConfigSchema>;

/**
 * Experience (number of deals) adjustment tiers
 */
export const ExperienceAdjustmentConfigSchema = z.object({
  isEnabled: z.boolean().default(true),
  tiers: z.array(AdjustmentTierSchema).default([
    {
      id: 'exp-11',
      label: 'Expert (11+ deals)',
      condition: { operator: 'gte', value: 11 },
      adjustment: -0.5,
      isEnabled: true,
    },
    {
      id: 'exp-4',
      label: 'Seasoned (4-10 deals)',
      condition: { operator: 'between', value: 4, maxValue: 10 },
      adjustment: -0.25,
      isEnabled: true,
    },
    {
      id: 'exp-1',
      label: 'Beginner (1-3 deals)',
      condition: { operator: 'between', value: 1, maxValue: 3 },
      adjustment: 0,
      isEnabled: true,
    },
    {
      id: 'exp-0',
      label: 'First-time (0 deals)',
      condition: { operator: 'eq', value: 0 },
      adjustment: 0.5,
      isEnabled: true,
    },
  ]),
});

export type ExperienceAdjustmentConfig = z.infer<typeof ExperienceAdjustmentConfigSchema>;

/**
 * Property type adjustment tiers
 */
export const PropertyTypeAdjustmentConfigSchema = z.object({
  isEnabled: z.boolean().default(true),
  tiers: z.array(AdjustmentTierSchema).default([
    {
      id: 'prop-sfr',
      label: 'Single Family',
      condition: { operator: 'eq', value: 'sfr' },
      adjustment: 0,
      isEnabled: true,
    },
    {
      id: 'prop-condo',
      label: 'Condo/Townhouse',
      condition: { operator: 'in', value: 'condo,townhouse' },
      adjustment: 0.125,
      isEnabled: true,
    },
    {
      id: 'prop-24',
      label: '2-4 Unit',
      condition: { operator: 'eq', value: '2-4_unit' },
      adjustment: 0.25,
      isEnabled: true,
    },
    {
      id: 'prop-mf',
      label: 'Multifamily (5+)',
      condition: { operator: 'eq', value: 'multifamily' },
      adjustment: 0.5,
      isEnabled: true,
    },
    {
      id: 'prop-comm',
      label: 'Commercial',
      condition: { operator: 'in', value: 'commercial,office,retail,industrial' },
      adjustment: 0.75,
      isEnabled: true,
    },
  ]),
});

export type PropertyTypeAdjustmentConfig = z.infer<typeof PropertyTypeAdjustmentConfigSchema>;

/**
 * Loan amount adjustment tiers
 */
export const LoanAmountAdjustmentConfigSchema = z.object({
  isEnabled: z.boolean().default(true),
  tiers: z.array(AdjustmentTierSchema).default([
    {
      id: 'amt-1m',
      label: 'Large ($1M+)',
      condition: { operator: 'gte', value: 1000000 },
      adjustment: -0.25,
      isEnabled: true,
    },
    {
      id: 'amt-500k',
      label: 'Standard ($500K-$1M)',
      condition: { operator: 'between', value: 500000, maxValue: 999999 },
      adjustment: 0,
      isEnabled: true,
    },
    {
      id: 'amt-small',
      label: 'Small (<$500K)',
      condition: { operator: 'lt', value: 500000 },
      adjustment: 0.25,
      isEnabled: true,
    },
  ]),
});

export type LoanAmountAdjustmentConfig = z.infer<typeof LoanAmountAdjustmentConfigSchema>;

/**
 * Loan purpose/product type adjustment tiers
 */
export const LoanPurposeAdjustmentConfigSchema = z.object({
  isEnabled: z.boolean().default(false),
  tiers: z.array(AdjustmentTierSchema).default([
    {
      id: 'purpose-bridge',
      label: 'Bridge/Fix & Flip',
      condition: { operator: 'eq', value: 'bridge' },
      adjustment: 0,
      isEnabled: true,
    },
    {
      id: 'purpose-rental',
      label: 'Rental/DSCR',
      condition: { operator: 'eq', value: 'rental' },
      adjustment: -0.25,
      isEnabled: true,
    },
    {
      id: 'purpose-construction',
      label: 'Ground-up Construction',
      condition: { operator: 'eq', value: 'construction' },
      adjustment: 0.5,
      isEnabled: true,
    },
  ]),
});

export type LoanPurposeAdjustmentConfig = z.infer<typeof LoanPurposeAdjustmentConfigSchema>;

// =============================================================================
// SPECIAL ADJUSTMENTS
// =============================================================================

export const SpecialAdjustmentsSchema = z.object({
  repeatBorrower: z.object({
    isEnabled: z.boolean().default(true),
    adjustment: z.number().default(-0.25),
    label: z.string().default('Repeat Borrower Discount'),
  }),
  crossCollateral: z.object({
    isEnabled: z.boolean().default(false),
    adjustment: z.number().default(-0.25),
    label: z.string().default('Cross-Collateralization Discount'),
  }),
  earlyPayoff: z.object({
    isEnabled: z.boolean().default(false),
    adjustment: z.number().default(-0.125),
    label: z.string().default('Early Payoff Track Record'),
  }),
});

export type SpecialAdjustments = z.infer<typeof SpecialAdjustmentsSchema>;

// =============================================================================
// ELIGIBILITY RULES
// =============================================================================

export const EligibilityRuleSchema = z.object({
  id: z.string(),
  field: z.string(), // e.g., 'fico', 'ltv', 'loanAmount'
  operator: ComparisonOperatorSchema,
  value: z.union([z.number(), z.string()]),
  maxValue: z.number().optional(),
  message: z.string(), // Error message when rule fails
  isHardStop: z.boolean().default(true), // Hard stop vs warning
});

export type EligibilityRule = z.infer<typeof EligibilityRuleSchema>;

// =============================================================================
// COMPLETE PRICING RULE CONFIGURATION
// =============================================================================

export const PricingRuleConfigSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),

  // Base Configuration
  baseRate: z.number().min(0).max(30).default(11.0),
  minLoanAmount: z.number().min(0).default(50000),
  maxLoanAmount: z.number().min(0).default(5000000),
  maxLTV: z.number().min(0).max(100).default(75),
  maxLTC: z.number().min(0).max(100).default(90),
  termOptions: z.array(z.number()).default([6, 9, 12, 18, 24]),

  // Adjustment Configurations
  ficoAdjustments: FicoAdjustmentConfigSchema,
  ltvAdjustments: LtvAdjustmentConfigSchema,
  experienceAdjustments: ExperienceAdjustmentConfigSchema,
  propertyTypeAdjustments: PropertyTypeAdjustmentConfigSchema,
  loanAmountAdjustments: LoanAmountAdjustmentConfigSchema,
  loanPurposeAdjustments: LoanPurposeAdjustmentConfigSchema,

  // Special Adjustments
  specialAdjustments: SpecialAdjustmentsSchema,

  // Eligibility Rules
  eligibilityRules: z.array(EligibilityRuleSchema).default([
    {
      id: 'min-fico',
      field: 'fico',
      operator: 'gte',
      value: 620,
      message: 'Minimum FICO score is 620',
      isHardStop: true,
    },
    { id: 'max-ltv', field: 'ltv', operator: 'lte', value: 75, message: 'Maximum LTV is 75%', isHardStop: true },
    { id: 'max-ltc', field: 'ltc', operator: 'lte', value: 90, message: 'Maximum LTC is 90%', isHardStop: true },
    {
      id: 'min-loan',
      field: 'loanAmount',
      operator: 'gte',
      value: 50000,
      message: 'Minimum loan amount is $50,000',
      isHardStop: true,
    },
  ]),

  // Metadata
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  createdBy: z.string().uuid().optional(),
});

export type PricingRuleConfig = z.infer<typeof PricingRuleConfigSchema>;

// =============================================================================
// DEFAULT PRICING RULE FACTORY
// =============================================================================

export function createDefaultPricingRule(businessId: string): PricingRuleConfig {
  return PricingRuleConfigSchema.parse({
    id: crypto.randomUUID(),
    businessId,
    name: 'Standard Pricing',
    description: 'Default pricing rules for loan products',
    isDefault: true,
    isActive: true,
    baseRate: 11.0,
    minLoanAmount: 50000,
    maxLoanAmount: 5000000,
    maxLTV: 75,
    maxLTC: 90,
    termOptions: [6, 9, 12, 18, 24],
    // Adjustment configs - empty objects will use schema defaults
    ficoAdjustments: {},
    ltvAdjustments: {},
    experienceAdjustments: {},
    propertyTypeAdjustments: {},
    loanAmountAdjustments: {},
    loanPurposeAdjustments: {},
    specialAdjustments: {
      repeatBorrower: {},
      crossCollateral: {},
      earlyPayoff: {},
    },
  });
}

// =============================================================================
// RATE CALCULATION WITH CONFIGURABLE RULES
// =============================================================================

export interface ConfigurableRateInput {
  purchasePrice: number;
  rehabBudget: number;
  afterRepairValue: number;
  requestedLoanAmount: number;
  estimatedFico: string | number;
  preferredTerm: number;
  flipsInLast2Years?: string | number;
  propertyType?: string;
  loanPurpose?: string;
  isRepeatBorrower?: boolean;
  hasCrossCollateral?: boolean;
  hasEarlyPayoffHistory?: boolean;
}

export interface RateAdjustment {
  category: string;
  description: string;
  adjustment: number;
}

export interface ConfigurableRateResult {
  estimatedRate: number;
  baseRate: number;
  totalAdjustment: number;
  ltv: number;
  ltc: number;
  monthlyPayment: number;
  totalCost: number;
  isEligible: boolean;
  ineligibilityReason?: string;
  adjustments: RateAdjustment[];
  warnings: string[];
}

/**
 * Parse FICO string to numeric value for comparison
 */
function parseFicoValue(fico: string | number): number {
  if (typeof fico === 'number') return fico;

  // Handle range strings like "740-759"
  if (fico.includes('-')) {
    const [min = '0'] = fico.split('-');
    return parseInt(min, 10);
  }
  // Handle "760+" format
  if (fico.includes('+')) {
    return parseInt(fico.replace('+', ''), 10);
  }
  // Handle "below-660" format
  if (fico.startsWith('below')) {
    return 650; // Approximate
  }
  return parseInt(fico, 10) || 0;
}

/**
 * Parse experience string to numeric value
 */
function parseExperienceValue(exp: string | number | undefined): number {
  if (exp === undefined) return 0;
  if (typeof exp === 'number') return exp;

  if (exp === '11+') return 11;
  if (exp === '4-10') return 7;
  if (exp === '1-3') return 2;
  if (exp === '0') return 0;
  return parseInt(exp, 10) || 0;
}

/**
 * Check if a value matches a tier condition
 */
function matchesTierCondition(value: number | string, condition: TierCondition): boolean {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  const condValue = typeof condition.value === 'number' ? condition.value : parseFloat(condition.value) || 0;

  switch (condition.operator) {
    case 'eq':
      return typeof value === 'string' && typeof condition.value === 'string'
        ? value === condition.value
        : numValue === condValue;
    case 'neq':
      return numValue !== condValue;
    case 'lt':
      return numValue < condValue;
    case 'lte':
      return numValue <= condValue;
    case 'gt':
      return numValue > condValue;
    case 'gte':
      return numValue >= condValue;
    case 'between':
      return numValue >= condValue && numValue <= (condition.maxValue ?? condValue);
    case 'in':
      if (typeof condition.value === 'string') {
        const values = condition.value.split(',').map((v) => v.trim());
        return values.includes(String(value));
      }
      return false;
    default:
      return false;
  }
}

/**
 * Find matching tier and return its adjustment
 */
function findTierAdjustment(
  value: number | string,
  config: { isEnabled: boolean; tiers: AdjustmentTier[] },
  category: string,
): RateAdjustment | null {
  if (!config.isEnabled) return null;

  for (const tier of config.tiers) {
    if (tier.isEnabled && matchesTierCondition(value, tier.condition)) {
      return {
        category,
        description: tier.label,
        adjustment: tier.adjustment,
      };
    }
  }
  return null;
}

/**
 * Calculate loan rate using configurable pricing rules
 */
export function calculateLoanRateWithConfig(
  input: ConfigurableRateInput,
  config: PricingRuleConfig,
): ConfigurableRateResult {
  const {
    purchasePrice,
    rehabBudget,
    afterRepairValue,
    requestedLoanAmount,
    estimatedFico,
    preferredTerm,
    flipsInLast2Years,
    propertyType,
    loanPurpose,
    isRepeatBorrower,
    hasCrossCollateral,
    hasEarlyPayoffHistory,
  } = input;

  // Calculate LTV and LTC
  const totalCost = purchasePrice + rehabBudget;
  const ltv = (requestedLoanAmount / afterRepairValue) * 100;
  const ltc = (requestedLoanAmount / totalCost) * 100;

  const warnings: string[] = [];
  const adjustments: RateAdjustment[] = [];

  // Check eligibility rules
  for (const rule of config.eligibilityRules) {
    let fieldValue: number;
    switch (rule.field) {
      case 'fico':
        fieldValue = parseFicoValue(estimatedFico);
        break;
      case 'ltv':
        fieldValue = ltv;
        break;
      case 'ltc':
        fieldValue = ltc;
        break;
      case 'loanAmount':
        fieldValue = requestedLoanAmount;
        break;
      default:
        continue;
    }

    if (!matchesTierCondition(fieldValue, { operator: rule.operator, value: rule.value, maxValue: rule.maxValue })) {
      if (rule.isHardStop) {
        return {
          estimatedRate: 0,
          baseRate: config.baseRate,
          totalAdjustment: 0,
          ltv,
          ltc,
          monthlyPayment: 0,
          totalCost: 0,
          isEligible: false,
          ineligibilityReason: rule.message,
          adjustments: [],
          warnings: [],
        };
      }
      warnings.push(rule.message);
    }
  }

  // Start with base rate
  let rate = config.baseRate;

  // FICO adjustments
  const ficoValue = parseFicoValue(estimatedFico);
  const ficoAdj = findTierAdjustment(ficoValue, config.ficoAdjustments, 'Credit Score');
  if (ficoAdj && ficoAdj.adjustment !== 0) {
    adjustments.push(ficoAdj);
    rate += ficoAdj.adjustment;
  }

  // LTV adjustments
  const ltvAdj = findTierAdjustment(ltv, config.ltvAdjustments, 'Loan-to-Value');
  if (ltvAdj && ltvAdj.adjustment !== 0) {
    adjustments.push(ltvAdj);
    rate += ltvAdj.adjustment;
  }

  // Experience adjustments
  const expValue = parseExperienceValue(flipsInLast2Years);
  const expAdj = findTierAdjustment(expValue, config.experienceAdjustments, 'Experience');
  if (expAdj && expAdj.adjustment !== 0) {
    adjustments.push(expAdj);
    rate += expAdj.adjustment;
  }

  // Property type adjustments
  if (propertyType) {
    const propAdj = findTierAdjustment(propertyType, config.propertyTypeAdjustments, 'Property Type');
    if (propAdj && propAdj.adjustment !== 0) {
      adjustments.push(propAdj);
      rate += propAdj.adjustment;
    }
  }

  // Loan amount adjustments
  const amtAdj = findTierAdjustment(requestedLoanAmount, config.loanAmountAdjustments, 'Loan Amount');
  if (amtAdj && amtAdj.adjustment !== 0) {
    adjustments.push(amtAdj);
    rate += amtAdj.adjustment;
  }

  // Loan purpose adjustments
  if (loanPurpose) {
    const purposeAdj = findTierAdjustment(loanPurpose, config.loanPurposeAdjustments, 'Loan Purpose');
    if (purposeAdj && purposeAdj.adjustment !== 0) {
      adjustments.push(purposeAdj);
      rate += purposeAdj.adjustment;
    }
  }

  // Special adjustments
  if (isRepeatBorrower && config.specialAdjustments.repeatBorrower.isEnabled) {
    const adj = config.specialAdjustments.repeatBorrower;
    adjustments.push({
      category: 'Special',
      description: adj.label,
      adjustment: adj.adjustment,
    });
    rate += adj.adjustment;
  }

  if (hasCrossCollateral && config.specialAdjustments.crossCollateral.isEnabled) {
    const adj = config.specialAdjustments.crossCollateral;
    adjustments.push({
      category: 'Special',
      description: adj.label,
      adjustment: adj.adjustment,
    });
    rate += adj.adjustment;
  }

  if (hasEarlyPayoffHistory && config.specialAdjustments.earlyPayoff.isEnabled) {
    const adj = config.specialAdjustments.earlyPayoff;
    adjustments.push({
      category: 'Special',
      description: adj.label,
      adjustment: adj.adjustment,
    });
    rate += adj.adjustment;
  }

  // Ensure rate doesn't go below a floor (e.g., 5%)
  rate = Math.max(rate, 5);

  // Calculate monthly payment (interest-only)
  const monthlyRate = rate / 100 / 12;
  const monthlyPayment = requestedLoanAmount * monthlyRate;

  // Calculate total cost over term
  const totalInterest = monthlyPayment * preferredTerm;
  const totalLoanCost = requestedLoanAmount + totalInterest;

  const totalAdjustment = adjustments.reduce((sum, adj) => sum + adj.adjustment, 0);

  return {
    estimatedRate: rate,
    baseRate: config.baseRate,
    totalAdjustment,
    ltv,
    ltc,
    monthlyPayment,
    totalCost: totalLoanCost,
    isEligible: true,
    adjustments,
    warnings,
  };
}
